import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.png';

interface Props {
  show: boolean;
}

const SplashScreen = ({ show }: Props) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!show) return;
    const start = Date.now();
    const duration = 1600;
    let frameId: number;

    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min((elapsed / duration) * 100, 100);
      setProgress(p);
      if (p < 100) {
        frameId = requestAnimationFrame(tick);
      }
    };
    
    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.03 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background overflow-hidden"
        >
          {/* Simplified background for low GPU load */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.02] to-transparent pointer-events-none" />

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Logo */}
            <motion.div
              className="relative"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 150, damping: 20, delay: 0.1 }}
            >
              {/* Glow ring */}
              <motion.div
                className="absolute -inset-3 rounded-[22px] bg-primary/10 blur-xl"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <img
                src={logo}
                alt="FixEveryday"
                className="h-[72px] w-[72px] sm:h-20 sm:w-20 md:h-24 md:w-24 rounded-full relative z-10"
              />
            </motion.div>

            {/* Brand name */}
            <motion.h1
              className="mt-5 text-[26px] sm:text-3xl md:text-4xl font-extrabold tracking-tight text-[#FF0000] italic"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              FixEveryday
            </motion.h1>

            {/* Tagline */}
            <motion.p
              className="mt-1.5 text-[11px] sm:text-xs text-muted-foreground font-medium tracking-[0.2em] uppercase"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5, ease: 'easeOut' }}
            >
              Expert services, one tap away
            </motion.p>

            {/* Trust indicators */}
            <motion.div
              className="flex items-center gap-4 mt-7"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {[
                { value: '10K+', label: 'Users' },
                { value: '4.8★', label: 'Rating' },
                { value: '50+', label: 'Services' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col items-center">
                  <span className="text-sm sm:text-base font-bold text-foreground">{stat.value}</span>
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground font-medium">{stat.label}</span>
                </div>
              ))}
            </motion.div>

            {/* Progress bar */}
            <motion.div
              className="mt-8 w-44 sm:w-52"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="h-[3px] rounded-full bg-border/50 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </motion.div>
          </div>

          {/* Bottom branding */}
          <motion.div
            className="absolute bottom-8 flex flex-col items-center gap-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <p className="text-[9px] sm:text-[10px] text-muted-foreground/40 font-medium tracking-wider uppercase">
              Trusted by thousands across India
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
